import React, { useState, useEffect, useMemo } from "react";
import {
  Search, Eye, Trash2, X, MessageSquare, BarChart3, ShieldAlert,
  Calendar, MapPin, Users, CheckCircle2, Ban, Clock3, Tag as TagIcon, Heart
} from 'lucide-react';
import { InputText } from 'primereact/inputtext';
import moment from 'moment';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AdminLoginAPi, deleteWithToken, fetchWithToken } from "api/ApiHandler";
import Sidebar1 from "components/Sidebar1";
import DEfulatImg from '../../Assests/Images/DefulatLogo.png'
const getAvatarUrl = (userId, { forceDefaultForServiceProvider = false } = {}) => {
  if (forceDefaultForServiceProvider && userId?.role === "service_provider") return DEfulatImg;
  const path = userId?.imagePath || userId?.image;
  if (!path || path === "image") return DEfulatImg;
  return path;
};

const getAuthorName = (userId) => {
  if (!userId) return "Unknown";
  if (userId.businessName) return userId.businessName;
  const name = `${userId.firstName || ""} ${userId.lastName || ""}`.trim();
  return name || "Unknown";
};

const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  const m = moment.utc(value);
  if (!m.isValid()) return "-";
  return m.format(withTime ? "DD/MM/YYYY hh:mm A" : "DD/MM/YYYY");
};

const eventStatusMeta = (status) => {
  switch ((status || "pending").toLowerCase()) {
    case "approved":
    case "live":
      return { label: "Live", className: "bg-green-50 text-[#12A54B]" };
    case "rejected":
      return { label: "Rejected", className: "bg-red-50 text-[#CE2920]" };
    default:
      return { label: "Pending", className: "bg-yellow-50 text-[#B8860B]" };
  }
};

const handleAuthError = (error) => {
  const status = error?.response?.status;
  const msg = error?.response?.data?.msg;
  if (status === 401 || msg === "Invalid Token") {
    localStorage.clear();
    window.location.href = "/admin-login";
    return true;
  }
  return false;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const m = moment.utc(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

export default function CommunityManagement() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [activeTab, setActiveTab] = useState("posts"); // 'posts' | 'events'
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [posts, setPosts] = useState([]);
  const [isPostsLoading, setIsPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState("");

  const [events, setEvents] = useState([]);
  const [isEventsLoading, setIsEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");

  const loadPosts = async () => {
    try {
      setIsPostsLoading(true);
      setPostsError("");
      const data = await fetchWithToken("/admin/get-all-posts");
      setPosts(Array.isArray(data?.posts) ? data.posts : []);
    } catch (error) {
      console.error("Fetch posts error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setPostsError("Unable to load posts.");
    } finally {
      setIsPostsLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      setIsEventsLoading(true);
      setEventsError("");
      const data = await fetchWithToken("/admin/get-all-events");
      setEvents(Array.isArray(data?.events) ? data.events : []);
    } catch (error) {
      console.error("Fetch events error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setEventsError("Unable to load events.");
    } finally {
      setIsEventsLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
    loadEvents();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, activeTab]);

  const filteredPosts = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return posts.filter((p) => {
      const matchesTerm =
        (p.description || "").toLowerCase().includes(term) ||
        getAuthorName(p.userId).toLowerCase().includes(term);
      const matchesDate = !dateFilter || toDateInputValue(p.createdAt) === dateFilter;
      return matchesTerm && matchesDate;
    });
  }, [posts, searchTerm, dateFilter]);

  const filteredEvents = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return events.filter((e) => {
      const matchesTerm =
        (e.eventName || "").toLowerCase().includes(term) ||
        (e.location?.text || "").toLowerCase().includes(term) ||
        getAuthorName(e.userId).toLowerCase().includes(term);
      const matchesDate = !dateFilter || toDateInputValue(e.eventDateTime) === dateFilter;
      return matchesTerm && matchesDate;
    });
  }, [events, searchTerm, dateFilter]);

  // --- PAGINATION LOGIC (shared across tabs) ---
  const activeList = activeTab === "posts" ? filteredPosts : filteredEvents;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = activeList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(activeList.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const approveEvent = async (id) => {
    try {
      await AdminLoginAPi("/admin/approve-event", { id });
      toast.success("Event approved and is now live.");
      setSelectedEvent((prev) => (prev && prev._id === id ? { ...prev, status: "approved" } : prev));
      loadEvents();
    } catch (error) {
      console.error("Approve event error:", error.response?.data || error);
      handleAuthError(error);
      // error toast is already shown by the global axios interceptor
    }
  };

  const rejectEvent = async (id) => {
    try {
      await AdminLoginAPi("/admin/reject-event", { id });
      toast.success("Event rejected.");
      setSelectedEvent((prev) => (prev && prev._id === id ? { ...prev, status: "rejected" } : prev));
      loadEvents();
    } catch (error) {
      console.error("Reject event error:", error.response?.data || error);
      handleAuthError(error);
    }
  };

  const deleteEvent = async (id) => {
    try {
      await deleteWithToken(`/admin/delete-event/${id}`);
      toast.success("Event deleted.");
      setSelectedEvent((prev) => (prev && prev._id === id ? null : prev));
      loadEvents();
    } catch (error) {
      console.error("Delete event error:", error.response?.data || error);
      handleAuthError(error);
    }
  };

  const deletePost = async (id) => {
    try {
      await deleteWithToken(`/admin/delete-post/${id}`);
      toast.success("Post deleted.");
      setSelectedPost((prev) => (prev && prev._id === id ? null : prev));
      loadPosts();
    } catch (error) {
      console.error("Delete post error:", error.response?.data || error);
      handleAuthError(error);
    }
  };

  const [confirmTarget, setConfirmTarget] = useState(null); // { type: 'post' | 'event', id }
  const requestDeletePost = (id) => setConfirmTarget({ type: "post", id });
  const requestDeleteEvent = (id) => setConfirmTarget({ type: "event", id });
  const cancelDelete = () => setConfirmTarget(null);
  const confirmDelete = async () => {
    if (!confirmTarget) return;
    const { type, id } = confirmTarget;
    setConfirmTarget(null);
    if (type === "post") await deletePost(id);
    else await deleteEvent(id);
  };

  const isDrawerOpen = !!selectedPost || !!selectedEvent;
  const closeDrawer = () => {
    setSelectedPost(null);
    setSelectedEvent(null);
  };

  const pendingCount = events.filter((e) => (e.status || "pending").toLowerCase() === "pending").length;
  const liveCount = events.filter((e) => ["approved", "live"].includes((e.status || "").toLowerCase())).length;
  const rejectedCount = events.filter((e) => (e.status || "").toLowerCase() === "rejected").length;

  return (
    <div className="flex h-screen bg-[#F9FAFB] overflow-hidden font-sans relative">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      {isDrawerOpen && (
        <div
          onClick={closeDrawer}
          className="fixed inset-0 bg-white/10 backdrop-blur-[1px] z-[40]"
        />
      )}
      <main className={`flex-1  flex flex-col min-w-0 overflow-hidden transition-all duration-500 ${isDrawerOpen ? 'blur-md pointer-events-none' : ''}`}>

        {/* Header */}
        <header className="flex items-center justify-between p-4 lg:px-5 lg:py-6" style={{paddingLeft:10,paddingBottom:'1em',paddingRight:24,paddingTop:'2em'}}>
          <h1 style={{fontFamily:'NeueMontreal'}} className="text-2xl lg:text-3xl font-medium text-gray-900">
            Community Management
          </h1>
          <div className="flex items-center gap-3 flex-wrap justify-end">
             <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <InputText
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={activeTab === "posts" ? "Search by author or post" : "Search events by name"}
                    className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm w-64 sm:w-40 outline-none"
                />
             </div>
             <div className="relative">
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    value={dateFilter ? moment(dateFilter) : null}
                    onChange={(newValue) => setDateFilter(newValue && newValue.isValid() ? newValue.format("YYYY-MM-DD") : "")}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: "small",
                        placeholder: "Filter by date",
                        sx: {
                          width: 192,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "#fff",
                            fontSize: "14px",
                            fontFamily: "'NeueMontreal', sans-serif",
                          },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                          "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#EA352B" },
                          "& .MuiInputBase-input": { padding: "8px 12px" },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                {dateFilter && (
                  <button
                    onClick={() => setDateFilter("")}
                    className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-300 hover:text-[#EA352B] transition z-10"
                    title="Clear date filter"
                  >
                    <X size={14} />
                  </button>
                )}
             </div>
             {/* <div className="w-10 h-10 rounded-xl overflow-hidden border shadow-sm">
                <img src="https://i.pravatar.cc/150?u=admin" alt="Admin" className="w-full h-full object-cover" />
             </div> */}
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 lg:px-5 pb-10" style={{paddingLeft:10}}>

          {/* --- TABS --- */}
          <div className="flex items-center gap-2 mb-6 bg-white w-fit p-1.5 rounded-2xl border border-gray-100" style={{boxShadow:'0px 0px 3px 0px #c3c3c3d1'}}>
            <button
              onClick={() => setActiveTab("posts")}
              style={{fontFamily:"'NeueMontreal', sans-serif", background: activeTab === "posts" ? '#FEE2E2' : undefined, color: activeTab === "posts" ? '#EA352B' : undefined}}
              className={`px-6 py-2 rounded-xl text-sm font-medium transition ${activeTab === "posts" ? 'shadow-sm' : 'text-gray-500 hover:text-black'}`}
            >
              All Posts
            </button>
            <button
              onClick={() => setActiveTab("events")}
              style={{fontFamily:"'NeueMontreal', sans-serif", background: activeTab === "events" ? '#FEE2E2' : undefined, color: activeTab === "events" ? '#EA352B' : undefined}}
              className={`px-6 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2 ${activeTab === "events" ? 'shadow-sm' : 'text-gray-500 hover:text-black'}`}
            >
              Events
              {pendingCount > 0 && (
                <span
                  style={activeTab === "events" ? { background: '#EA352B', color: '#FFFFFF' } : { background: '#FEE2E2', color: '#EA352B' }}
                  className="text-[10px]  px-2 py-0.5 rounded-full"
                >
                  {pendingCount} pending
                </span>
              )}
            </button>
          </div>

          {activeTab === "posts" ? (
            <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6 mb-8">
              <StatCard label="Total Posts" value={posts.length} loading={isPostsLoading} icon={<MessageSquare size={20} className="text-red-500" />} />
              <StatCard label="Tips Shared" value={posts.filter((p) => p.postType === 'tip').length} loading={isPostsLoading} icon={<ShieldAlert size={20} className="text-red-500" />} />
              <StatCard label="Alerts Shared" value={posts.filter((p) => p.postType === 'alert').length} loading={isPostsLoading} icon={<BarChart3 size={20} className="text-red-500" />} />
            </div>
          ) : (
            <div className="grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-6 mb-8">
              <StatCard label="Total Events" value={events.length} loading={isEventsLoading} icon={<Calendar size={20} className="text-red-500" />} />
              <StatCard label="Pending Approval" value={pendingCount} loading={isEventsLoading} icon={<Clock3 size={20} className="text-red-500" />} />
              <StatCard label="Live Events" value={liveCount} loading={isEventsLoading} icon={<CheckCircle2 size={20} className="text-red-500" />} />
              <StatCard label="Rejected Events" value={rejectedCount} loading={isEventsLoading} icon={<Ban size={20} className="text-red-500" />} />
            </div>
          )}

          <div style={{background:'white',boxShadow:'0px 0px 3px 0px #c3c3c3d1'}} className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm overflow-hidden min-h-[600px] flex flex-col">
            <div className="flex-1 overflow-x-auto">
              {activeTab === "posts" ? (
                <table className="w-full text-left border-separate border-spacing-y-3">
                  <thead>
                    <tr style={{fontFamily:"'NeueMontreal', sans-serif"}} className="text-black text-[15px] font-light">
                      <th className="pb-4 pl-4 font-normal">Post</th>
                      <th className="pb-4 font-normal">Author</th>
                      <th className="pb-4 font-normal">Date</th>
                      <th className="pb-4 font-normal">Type</th>
                      <th className="pb-4 text-center font-normal">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isPostsLoading ? (
                      Array.from({ length: 6 }).map((_, i) => <PostRowSkeleton key={i} />)
                    ) : postsError ? (
                      <tr><td colSpan={5} className="py-10 text-center text-sm text-red-400">{postsError}</td></tr>
                    ) : currentItems.length === 0 ? (
                      <tr><td colSpan={5} className="py-10 text-center text-sm text-gray-400">No posts found.</td></tr>
                    ) : currentItems.map((item) => (
                      <tr key={item._id} className="group  transition-all rounded-2xl">
                        <td className="py-4 pl-4 text-sm max-w-[280px] truncate font-light text-gray-700">{item.description}</td>
                        <td className="py-4">
                          <div className="flex items-center gap-2">
                            <img src={getAvatarUrl(item.userId)} onError={(e) => { e.currentTarget.src = DEfulatImg; }} className="w-7 h-7 rounded-full object-cover" alt="" />
                            <span style={{fontFamily:"'NeueMontreal', sans-serif"}} className="text-[13px] font-medium">{getAuthorName(item.userId)}</span>
                          </div>
                        </td>
                        <td style={{fontFamily:"'NeueMontreal', sans-serif"}} className="py-4 text-[13px] text-gray-500">{formatDate(item.createdAt)}</td>
                        <td className="py-4">
                          <span style={{fontFamily:"'NeueMontreal', sans-serif"}} className={`px-4 py-1 rounded-full text-[11px] font-medium capitalize ${item.postType === 'alert' ? 'bg-orange-50 text-orange-500' : 'bg-blue-50 text-blue-500'}`}>
                            {item.postType || "post"}
                          </span>
                        </td>
                        <td className="py-4">
                          <div className="flex  justify-center gap-2">
                            <button style={{color:'white',fontFamily:'NeueMontreal'}} onClick={() => setSelectedPost(item)} className="bg-[#2D2D2D] text-white px-4 py-1.5 rounded-lg text-[11px] flex items-center gap-1.5 hover:opacity-85 transition">
                              <Eye size={12}/> View
                            </button>
                            <button style={{color:'white',fontFamily:'NeueMontreal'}} onClick={() => requestDeletePost(item._id)} className="bg-[#CE2920] text-white px-4 py-1.5 rounded-lg text-[11px] flex items-center gap-1.5 hover:opacity-85 transition">
                              <Trash2 size={12}/> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-left border-separate border-spacing-y-3">
                  <thead>
                    <tr style={{fontFamily:"'NeueMontreal', sans-serif"}} className="text-black text-[15px] font-light">
                      <th className="pb-4 pl-4 font-normal">Event</th>
                      <th className="pb-4 font-normal">Location</th>
                      <th className="pb-4 font-normal">Date</th>
                      <th className="pb-4 font-normal">Joined</th>
                      <th className="pb-4 font-normal">Status</th>
                      <th className="pb-4 text-center font-normal">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isEventsLoading ? (
                      Array.from({ length: 6 }).map((_, i) => <EventRowSkeleton key={i} />)
                    ) : eventsError ? (
                      <tr><td colSpan={6} className="py-10 text-center text-sm text-red-400">{eventsError}</td></tr>
                    ) : currentItems.length === 0 ? (
                      <tr><td colSpan={6} className="py-10 text-center text-sm text-gray-400">No events found.</td></tr>
                    ) : currentItems.map((item) => {
                      const status = eventStatusMeta(item.status);
                      const isApproved = ["approved", "live"].includes((item.status || "").toLowerCase());
                      return (
                        <tr key={item._id} className="group transition-all rounded-2xl">
                          <td className="py-4 pl-4 text-sm max-w-[240px] truncate font-medium text-gray-800">
                            <div className="flex items-center gap-2">
                              <img src={getAvatarUrl(item.userId, { forceDefaultForServiceProvider: true })} onError={(e) => { e.currentTarget.src = DEfulatImg; }} className="w-7 h-7 rounded-full object-cover" alt="" />
                              <span className="truncate">{item.eventName}</span>
                            </div>
                          </td>
                          <td style={{fontFamily:"'NeueMontreal', sans-serif"}} className="py-4 text-[13px] text-gray-500 max-w-[180px] truncate">{item.location?.text}</td>
                          <td style={{fontFamily:"'NeueMontreal', sans-serif"}} className="py-4 text-[13px] text-gray-500">{formatDate(item.eventDateTime)}</td>
                          <td style={{fontFamily:"'NeueMontreal', sans-serif"}} className="py-4 text-[13px] text-gray-700">
                            <div className="flex items-center gap-1.5">
                              <Users size={13} className="text-gray-400" /> {item.going?.length || 0}
                            </div>
                          </td>
                          <td className="py-4">
                            <span style={{fontFamily:"'NeueMontreal', sans-serif"}} className={`px-4 py-1 rounded-full text-[11px] font-medium ${status.className}`}>
                              {status.label}
                            </span>
                          </td>
                          <td className="py-4">
                            <div className="flex justify-center gap-2">
                              <button style={{color:'white',fontFamily:'NeueMontreal'}} onClick={() => setSelectedEvent(item)} className="bg-[#2D2D2D] text-white px-4 py-1.5 rounded-lg text-[11px] flex items-center gap-1.5 hover:opacity-85 transition">
                                <Eye size={12}/> View
                              </button>
                              {!isApproved && (
                                <button style={{color:'white',fontFamily:'NeueMontreal'}} onClick={() => approveEvent(item._id)} className="bg-[#17A210] text-white px-4 py-1.5 rounded-lg text-[11px] flex items-center gap-1.5 hover:opacity-85 transition">
                                  <CheckCircle2 size={12}/> Approve
                                </button>
                              )}
                              <button style={{color:'white',fontFamily:'NeueMontreal'}} onClick={() => requestDeleteEvent(item._id)} className="bg-[#CE2920] text-white px-4 py-1.5 rounded-lg text-[11px] flex items-center gap-1.5 hover:opacity-85 transition">
                                <Trash2 size={12}/> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* --- FUNCTIONAL PAGINATION SECTION --- */}
            {totalPages > 1 && (
              <div className="flex justify-end mt-10 gap-2 items-center">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => paginate(currentPage - 1)}
                    className={`text-xs font-medium px-2 py-1 transition ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-black'}`}
                  >
                    ← Previous
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      style={{fontFamily:"'NeueMontreal', sans-serif",color:currentPage === i + 1 ? 'white' : 'black'}}
                      onClick={() => paginate(i + 1)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition ${
                        currentPage === i + 1
                        ? 'bg-[#EA352B] text-white shadow-lg font-bold'
                        : 'hover:bg-gray-100 text-gray-500'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => paginate(currentPage + 1)}
                    className={`text-xs font-bold px-2 py-1 transition ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-black'}`}
                  >
                    Next →
                  </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* --- RIGHT SIDEBAR DETAIL DRAWER --- */}
      <div className={`fixed top-0 right-0 h-full w-[420px] bg-white shadow-[-20px_0_50px_rgba(0,0,0,0.1)] z-[50] transition-transform duration-500 ease-out transform ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div style={{background:'#F8F8F8'}} className="p-10 h-full flex flex-col relative overflow-y-auto">
            <button onClick={closeDrawer} className="absolute top-8 right-8 text-gray-300 hover:text-black transition">
              <X size={28} style={{color:'#EA352B'}} />
            </button>

            {selectedPost && (
              <div className="mt-8 flex-1 flex flex-col">
                <div className="flex items-center gap-4 mb-6">
                  <img src={getAvatarUrl(selectedPost.userId)} onError={(e) => { e.currentTarget.src = DEfulatImg; }} className="w-14 h-14 rounded-full object-cover border border-gray-100 shadow-sm" alt="" />
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">{getAuthorName(selectedPost.userId)}</h3>
                    <p className="text-gray-400 text-[11px] font-light capitalize">{selectedPost.userId?.role?.replace('_', ' ')} · {formatDate(selectedPost.createdAt, true)}</p>
                  </div>
                </div>

                <span style={{fontFamily:"'NeueMontreal', sans-serif"}} className={`w-fit px-4 py-1 rounded-full text-[11px] font-medium capitalize mb-4 ${selectedPost.postType === 'alert' ? 'bg-orange-50 text-orange-500' : 'bg-blue-50 text-blue-500'}`}>
                  {selectedPost.postType || "post"}
                </span>

                <p className="text-[15px] text-gray-600 leading-[1.6] mb-6 font-light whitespace-pre-line">
                  {selectedPost.description}
                </p>

                {selectedPost.image && (
                  <div className="w-full h-48 rounded-[32px] overflow-hidden mb-6 border border-gray-100">
                     <img src={selectedPost.image} className="w-full h-full object-cover" alt="" />
                  </div>
                )}

                {selectedPost.tags?.length > 0 && (
                  <div className="flex items-start gap-3 mb-6">
                    <TagIcon size={16} className="text-[#EA352B] mt-1" />
                    <div className="flex flex-wrap gap-2">
                      {selectedPost.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-[11px] text-gray-600">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-gray-600 mb-10">
                  <Heart size={16} className="text-[#EA352B]" />
                  <span>{selectedPost.likes?.length || 0} likes</span>
                </div>

                <div className="flex gap-3 mt-auto">
                  <button
                    style={{color:'white',fontFamily:'NeueMontreal'}}
                    onClick={() => requestDeletePost(selectedPost._id)}
                    className="flex-1 bg-[#CE2920] text-white py-3.5 rounded-2xl font-light text-sm shadow-sm hover:opacity-90 transition flex items-center justify-center gap-2"
                  >
                    <Trash2 size={16} /> Delete Post
                  </button>
                </div>
              </div>
            )}

            {selectedEvent && (
              <div className="mt-8 flex-1 flex flex-col">
                <div className="flex items-center gap-4 mb-6">
                  <img src={getAvatarUrl(selectedEvent.userId, { forceDefaultForServiceProvider: true })} onError={(e) => { e.currentTarget.src = DEfulatImg; }} className="w-14 h-14 rounded-full object-cover border border-gray-100 shadow-sm" alt="" />
                  <div>
                    <p className="text-gray-400 text-[11px] font-light">Organized by</p>
                    <h3 className="font-bold text-sm text-gray-900">{getAuthorName(selectedEvent.userId)}</h3>
                  </div>
                  <span style={{fontFamily:"'NeueMontreal', sans-serif"}} className={`ml-auto px-3 py-1 rounded-full text-[11px] font-medium ${eventStatusMeta(selectedEvent.status).className}`}>
                    {eventStatusMeta(selectedEvent.status).label}
                  </span>
                </div>

                <h2 style={{fontFamily:'NeueMontreal'}} className="text-2xl font-medium text-gray-900 mb-3">
                  {selectedEvent.eventName}
                </h2>

                <p className="text-[14px] text-gray-600 leading-[1.6] mb-6 font-light whitespace-pre-line">
                  {selectedEvent.description}
                </p>

                <div className="flex flex-col gap-3 mb-6">
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <Calendar size={16} className="text-[#EA352B]" />
                    <span>{formatDate(selectedEvent.eventDateTime, true)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <MapPin size={16} className="text-[#EA352B]" />
                    <span>{selectedEvent.location?.text}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <Users size={16} className="text-[#EA352B]" />
                    <span>{selectedEvent.going?.length || 0} people joined</span>
                  </div>
                </div>

                {selectedEvent.tags?.length > 0 && (
                  <div className="flex items-start gap-3 mb-10">
                    <TagIcon size={16} className="text-[#EA352B] mt-1" />
                    <div className="flex flex-wrap gap-2">
                      {selectedEvent.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-[11px] text-gray-600">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-auto">
                  {(selectedEvent.status || "pending").toLowerCase() === "pending" && (
                    <div className="flex gap-3">
                      <button
                        style={{color:'white',fontFamily:'NeueMontreal'}}
                        onClick={() => approveEvent(selectedEvent._id)}
                        className="flex-1 bg-[#17A210] text-white py-3.5 rounded-2xl font-light text-sm shadow-sm hover:opacity-90 transition flex items-center justify-center gap-2"
                      >
                        <CheckCircle2 size={16} /> Approve
                      </button>
                      <button
                        style={{color:'white',fontFamily:'NeueMontreal'}}
                        onClick={() => rejectEvent(selectedEvent._id)}
                        className="flex-1 bg-[#EA352B] text-white py-3.5 rounded-2xl font-light text-sm shadow-sm hover:opacity-90 transition flex items-center justify-center gap-2"
                      >
                        <Ban size={16} /> Reject
                      </button>
                    </div>
                  )}

                  {["approved", "live"].includes((selectedEvent.status || "").toLowerCase()) && (
                    <>
                      <button
                        style={{color:'white',fontFamily:'NeueMontreal'}}
                        onClick={() => requestDeleteEvent(selectedEvent._id)}
                        className="w-full bg-[#CE2920] text-white py-3.5 rounded-2xl font-light text-sm shadow-sm hover:opacity-90 transition flex items-center justify-center gap-2"
                      >
                        <Trash2 size={16} /> Delete Event
                      </button>
                      <button
                        onClick={() => rejectEvent(selectedEvent._id)}
                        className="w-full text-center text-[12px] text-[#EA352B] mt-3 underline"
                      >
                        Revoke approval
                      </button>
                    </>
                  )}

                  {(selectedEvent.status || "").toLowerCase() === "rejected" && (
                    <div className="flex gap-3">
                      <button
                        style={{color:'white',fontFamily:'NeueMontreal'}}
                        onClick={() => approveEvent(selectedEvent._id)}
                        className="flex-1 bg-[#17A210] text-white py-3.5 rounded-2xl font-light text-sm shadow-sm hover:opacity-90 transition flex items-center justify-center gap-2"
                      >
                        <CheckCircle2 size={16} /> Approve
                      </button>
                      <button
                        style={{color:'white',fontFamily:'NeueMontreal'}}
                        onClick={() => requestDeleteEvent(selectedEvent._id)}
                        className="flex-1 bg-[#CE2920] text-white py-3.5 rounded-2xl font-light text-sm shadow-sm hover:opacity-90 transition flex items-center justify-center gap-2"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* --- DELETE CONFIRMATION MODAL --- */}
      {confirmTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div style={{boxShadow:'0px 10px 40px rgba(0,0,0,0.15)'}} className="bg-white rounded-3xl p-8 w-full max-w-[380px]">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-5">
              <Trash2 size={24} className="text-[#EA352B]" />
            </div>
            <h3 style={{fontFamily:'NeueMontreal'}} className="text-lg font-medium text-gray-900 mb-2">Are you sure?</h3>
            <p className="text-sm text-gray-500 mb-8 leading-relaxed">
              This will permanently delete this {confirmTarget.type}. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 py-3 rounded-2xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{color:'white',fontFamily:'NeueMontreal'}}
                className="flex-1 py-3 rounded-2xl bg-[#CE2920] text-white text-sm font-medium hover:opacity-90 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function StatCard({ label, value, sub, icon, loading }) {
  return (
    <div style={{background:'white',boxShadow:'0px 0px 3px 0px #c3c3c3d1'}} className="bg-white p-5 rounded-[12px] border border-gray-50 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <p style={{fontFamily:"'NeueMontreal', sans-serif"}} className="text-[rgba(0, 0, 0, 0.75)] text-[13px] font-normal">{label}</p>
        {/* <div className="p-2 bg-red-50 rounded-xl">{icon}</div> */}
      </div>
      {loading ? (
        <div className="h-11 w-16 bg-gray-100 rounded-lg animate-pulse mb-1" />
      ) : (
        <h2 style={{fontFamily:"'NeueMontreal', sans-serif"}} className="text-5xl font-medium text-black mb-1">{value}</h2>
      )}
      {sub && <p className="text-[10px] text-green-500 font-bold uppercase tracking-wider">{sub}</p>}
    </div>
  );
}

function PostRowSkeleton() {
  return (
    <tr>
      <td className="py-4 pl-4"><div className="h-4 bg-gray-100 rounded-md animate-pulse w-56 max-w-full" /></td>
      <td className="py-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gray-100 animate-pulse" />
          <div className="h-3 w-20 bg-gray-100 rounded-md animate-pulse" />
        </div>
      </td>
      <td className="py-4"><div className="h-3 w-16 bg-gray-100 rounded-md animate-pulse" /></td>
      <td className="py-4"><div className="h-5 w-14 bg-gray-100 rounded-full animate-pulse" /></td>
      <td className="py-4">
        <div className="flex justify-center gap-2">
          <div className="h-7 w-16 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-7 w-16 bg-gray-100 rounded-lg animate-pulse" />
        </div>
      </td>
    </tr>
  );
}

function EventRowSkeleton() {
  return (
    <tr>
      <td className="py-4 pl-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gray-100 animate-pulse" />
          <div className="h-4 w-40 bg-gray-100 rounded-md animate-pulse" />
        </div>
      </td>
      <td className="py-4"><div className="h-3 w-28 bg-gray-100 rounded-md animate-pulse" /></td>
      <td className="py-4"><div className="h-3 w-16 bg-gray-100 rounded-md animate-pulse" /></td>
      <td className="py-4"><div className="h-3 w-8 bg-gray-100 rounded-md animate-pulse" /></td>
      <td className="py-4"><div className="h-5 w-16 bg-gray-100 rounded-full animate-pulse" /></td>
      <td className="py-4">
        <div className="flex justify-center gap-2">
          <div className="h-7 w-16 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-7 w-16 bg-gray-100 rounded-lg animate-pulse" />
        </div>
      </td>
    </tr>
  );
}
